import {useMemo, useCallback} from 'react';
import {IProduct, IProductOption} from '../../../../../types/galleryTypes';
import {IProductSelectionAvailabilityMap} from '@wix/wixstores-client-core/dist/es/src/productVariantCalculator/ProductVariantCalculator';
import {isOptionRequired} from '@wix/wixstores-client-core/dist/es/src/productOptions/productUtils';
import type {IProductOptionsItem} from '@wix/wixstores-client-core/dist/es/src/types/product';
import {useGlobals} from '../../../../../globalPropsContext';
import {Experiments} from '../../../../../constants';

interface UseOptionSelectionLogicParams {
  option: IProductOption;
  product?: IProduct;
  optionalModifiersEnabled?: boolean;
  selectionIds: number[];
  selectionsAvailability: IProductSelectionAvailabilityMap[string];
}

const getSelectionIdsFromOtherOptions = (selectionIds: number[], visibleSelections: IProductOption['selections']) => {
  return selectionIds.filter((selectionId) => !visibleSelections?.some(({id}) => id === selectionId));
};

const getNextSelectionIds = ({
  pickedSelectionId,
  currentSelectionIds,
  visibleSelections,
  isRequired,
  optionalModifiersEnabled,
}: {
  pickedSelectionId: number | null;
  currentSelectionIds: number[];
  visibleSelections: IProductOption['selections'];
  isRequired: boolean;
  optionalModifiersEnabled: boolean;
}) => {
  const selectionIdsFromOtherOptions = getSelectionIdsFromOtherOptions(currentSelectionIds, visibleSelections);

  if (pickedSelectionId === null) {
    return selectionIdsFromOtherOptions;
  }

  if (!optionalModifiersEnabled || isRequired || !currentSelectionIds.includes(pickedSelectionId)) {
    return [...selectionIdsFromOtherOptions, pickedSelectionId];
  }

  return selectionIdsFromOtherOptions;
};

export const useOptionSelectionLogic = ({
  option,
  product,
  optionalModifiersEnabled = false,
  selectionIds,
  selectionsAvailability,
}: UseOptionSelectionLogicParams) => {
  let isRequired = true;
  if (optionalModifiersEnabled && product) {
    isRequired = isOptionRequired(product, option as unknown as IProductOptionsItem, true);
  }
  const {useExperiments, textsMap} = useGlobals();
  const showHiddenVariants = useExperiments.enabled(Experiments.ShowHiddenVariants);

  const visibleSelections = useMemo(
    () =>
      option?.selections?.filter(
        (selection) => showHiddenVariants || selectionsAvailability?.[selection.id]?.isVisible
      ) || [],
    [option.selections, selectionsAvailability, showHiddenVariants]
  );

  const selectedSelectionId = useMemo(
    () => option?.selections?.find((selection) => selectionIds?.includes(selection.id))?.id.toString(),
    [option.selections, selectionIds]
  );

  const isSelectionDisabled = useCallback(
    (selectionId: number) => !showHiddenVariants && !selectionsAvailability?.[selectionId]?.isSelectable,
    [selectionsAvailability, showHiddenVariants]
  );

  const isSelectionUnreachable = useCallback(
    (selectionId: number) =>
      !selectionsAvailability?.[selectionId]?.isSelectable ||
      (showHiddenVariants && selectionsAvailability?.[selectionId]?.isVisible === false),
    [selectionsAvailability, showHiddenVariants]
  );

  const isSelectionMarkedUnavailable = useCallback(
    (selectionId: number) => showHiddenVariants && isSelectionUnreachable(selectionId),
    [showHiddenVariants, isSelectionUnreachable]
  );

  const getSelectionAriaLabel = useCallback(
    (selectionId: number, description: string) =>
      isSelectionMarkedUnavailable(selectionId) ? `${description}, ${textsMap.productUnavailableText}` : description,
    [isSelectionMarkedUnavailable, textsMap.productUnavailableText]
  );

  const isSelectionChecked = useCallback((selectionId: number) => selectionIds?.includes(selectionId), [selectionIds]);

  const calculateNextSelectionIds = useCallback(
    (pickedSelectionId: number | null) => {
      const currentSelectionIds = selectionIds ?? /* istanbul ignore next */ [];
      return getNextSelectionIds({
        pickedSelectionId,
        currentSelectionIds,
        visibleSelections,
        isRequired,
        optionalModifiersEnabled,
      });
    },
    [isRequired, optionalModifiersEnabled, selectionIds, visibleSelections]
  );

  return {
    isRequired,
    visibleSelections,
    selectedSelectionId,
    isSelectionDisabled,
    isSelectionUnreachable,
    isSelectionMarkedUnavailable,
    getSelectionAriaLabel,
    isSelectionChecked,
    calculateNextSelectionIds,
  };
};
